import { FileSpreadsheet } from 'lucide-react'

import { Button, CheckboxField } from '@krp/ui-kit'

import { CommandStatus } from '../../../shared/ui/CommandStatus'
import { InputField } from '../../../shared/ui/InputField'
import { WarehouseSelect } from '../../structure/ui/WarehouseSelect'
import { useUnsavedGuard } from '../../../shared/ui/useUnsavedGuard'
import type { SupplyCreateActions, SupplyCreateVm } from '../model/viewModels'
import '../../../shared/ui/onec.css'
import '../../pricing/ui/onec-ui.css'
import './supplies.css'

/**
 * Новая поставка в базе окна. Склад и его организация выбираются ВНУТРИ этой
 * базы — из её складов, прочитанных из 1С; другой базы здесь нет и быть не может.
 * 1С не ответила — у склада её текст и «Повторить», склад можно выбрать позже.
 * Поставщик — ключ 1С (справочника контрагентов служба не публикует) или «без поставщика».
 * Открытое из окна файла накладной, окно создаёт поставку и сразу заполняет её строки из этого файла.
 */
export function SupplyCreateScreen({ vm, actions }: { vm: SupplyCreateVm; actions: SupplyCreateActions }) {
  const sending = vm.command?.status === 'sending' || vm.command?.status === 'unknown'
  useUnsavedGuard(sending)
  const title = vm.invoice === null ? 'Новая поставка' : 'Поставка из накладной'
  return (
    <section className="onec-screen" aria-label={`${title} 1С`}>
      <div className="onec-toolbar">
        <h2>{title}</h2>
        <Button variant="ghost" onClick={actions.close}>
          Отмена
        </Button>
      </div>
      <div className="onec-form">
        {vm.invoice !== null && (
          <div className="onec-note" role="note" aria-label="Файл накладной">
            <FileSpreadsheet size={16} aria-hidden="true" />
            <p>
              Строки — из файла накладной <code>{vm.invoice.fileId}</code>: заполнятся сразу после создания поставки, итог —
              в окне поставки.
            </p>
            {vm.invoice.problem !== null && <p className="onec-problem">{vm.invoice.problem}</p>}
          </div>
        )}
        <InputField
          label="Дата документа"
          type="date"
          value={vm.form.documentDate}
          onChange={(documentDate) => actions.changed({ documentDate })}
          disabled={sending}
        />
        <WarehouseSelect
          label="Склад приёмки"
          value={vm.form.warehouseKey}
          pick={vm.warehouses}
          placeholder="Выбрать позже"
          onChange={(warehouseKey) => actions.changed({ warehouseKey })}
          onRetry={actions.reloadWarehouses}
        />
        <CheckboxField
          label="Без поставщика"
          checked={vm.form.withoutSupplier}
          onChange={(withoutSupplier) => actions.changed({ withoutSupplier })}
        />
        {!vm.form.withoutSupplier && (
          <div className="onec-row">
            <InputField
              label="Ключ поставщика в 1С (GUID)"
              value={vm.form.supplierKey}
              onChange={(supplierKey) => actions.changed({ supplierKey })}
              disabled={sending}
            />
            <InputField
              label="Наименование поставщика"
              value={vm.form.supplierName}
              onChange={(supplierName) => actions.changed({ supplierName })}
              disabled={sending}
            />
          </div>
        )}
        <InputField
          label="Номер накладной поставщика"
          value={vm.form.supplierInvoiceNumber}
          onChange={(supplierInvoiceNumber) => actions.changed({ supplierInvoiceNumber })}
          disabled={sending}
        />
        <InputField label="Комментарий" value={vm.form.comment} onChange={(comment) => actions.changed({ comment })} disabled={sending} />
        {vm.problem !== null && <p className="onec-problem">{vm.problem}</p>}
        <div className="onec-actions">
          <Button variant="primary" onClick={actions.submit} disabled={!vm.canSubmit} busy={vm.command?.status === 'sending'}>
            {vm.invoice === null ? 'Создать поставку' : 'Создать и заполнить из накладной'}
          </Button>
        </div>
      </div>
      {vm.command !== null && (
        <CommandStatus
          command={vm.command}
          onResend={() => vm.command !== null && actions.resend(vm.command.key)}
          onDismiss={() => vm.command !== null && actions.dismiss(vm.command.key)}
        >
          {vm.created !== null && (
            <Button variant="primary" onClick={actions.openCreated}>
              Открыть поставку {vm.created.number}
            </Button>
          )}
        </CommandStatus>
      )}
    </section>
  )
}
